import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { agileProjectPrognoses, agileUren, combineerPrognose, fmtDag, fmtUren, geplandeUren, projectPrognose } from '@ep/core';
import { api } from '../lib/api';
import { PROJECT_STATUS, useActie, useData, useMag, useTaakOpenen } from '../lib/data';
import { ProjectGantt } from '../ui/ProjectGantt';
import { TakenTabel, urenVan } from '../ui/TakenTabel';
import { toast } from '../ui/toast';
import { Badge, Balk, Laden, Modal, VerwijderKnop } from '../ui/ui';
import { ProjectForm } from './ProjectForm';
import { MeerMenu } from '../ui/Shell';
import { EigenVeldenOverzicht } from '../ui/EigenVelden';
import { Gesprek } from '../ui/Gesprek';
import { Icoon } from '../ui/Icoon';

export function ProjectDetail() {
  const { id } = useParams();
  const { data: d, kal } = useData();
  const mag = useMag();
  const nav = useNavigate();
  const taak = useTaakOpenen();
  const [bewerk, setBewerk] = useState(false);
  const [pop, setPop] = useState<'taken' | 'tijdlijn' | null>(null);
  const p = d?.projecten.find((x) => x.id === id);
  const taken = useMemo(() => (d && p ? d.taken.filter((t) => t.projectId === p.id).sort((a, b) => (a.startdatum ?? '9').localeCompare(b.startdatum ?? '9')) : []), [d, p]);
  const prog = useMemo(() => (kal && d && p ? projectPrognose(kal, p, d.taken, d.vandaag, undefined, d.takenpakketten) : null), [kal, d, p]);
  // SPEC §9.2: agile werkpakketten tellen mee in uren en prognose
  const agile = useMemo(
    () => (kal && d && p ? agileProjectPrognoses({ kal, projectId: p.id, werkpakketten: d.werkpakketten, items: d.takenpakketten, sprints: d.sprints ?? [], vandaag: d.vandaag }).filter((x) => x.rest > 0) : []),
    [kal, d, p],
  );
  const verwijder = useActie(() => api.del(`/api/projecten/${id}`), { succes: 'Project verwijderd', opNa: () => nav('/projecten') });

  if (!d) return <Laden />;
  if (!p) return <div className="pagina"><div className="paneel leeg">Project niet gevonden. <Link to="/projecten">Terug</Link></div></div>;

  const budget = p.urenBudget ?? p.expertiseBudget.reduce((s, b) => s + b.uren, 0);
  const ag = agileUren(d.takenpakketten.filter((t) => t.projectId === p.id));
  // SPEC §11.3: bij agile gaan alleen geschreven uren van het budget af (items + overleg op het werkpakket)
  const agBesteed = ag.besteed + (p.besteedLosMin ?? 0) / 60;
  const gepland = geplandeUren(taken) + agBesteed;
  const klaar = geplandeUren(taken.filter((t) => t.terugmelding === 'KLAAR')) + ag.klaar;
  const totaalProg = prog ? combineerPrognose(prog.prognose, prog.laterDanHorizon, agile.map((x) => x.eind)) : null;
  const agileBepalend = totaalProg?.datum ? agile.find((x) => x.eind === totaalProg.datum && (!prog?.prognose || (x.eind ?? '') > prog.prognose)) : undefined;
  const nodig = taken.filter((t) => t.terugmelding === 'NIET_KLAAR' || t.terugmelding === 'HERPLANNEN').reduce((s, t) => s + (t.tmRestUren ?? 0), 0);
  const wacht = geplandeUren(taken.filter((t) => t.status === 'GEPLAND' && !t.terugmelding && t.taakType !== 'OVERLEG' && t.taakType !== 'FOCUS' && (t.einddatum ?? '') < d.vandaag));
  const rest = budget - gepland - nodig;

  const excel = async () => {
    const kop = ['Taak', 'Engineer', 'Start', 'Eind', 'Van', 'Tot', 'Uren', 'Status'];
    const rij = taken.map((t) => [t.titel, d.engineers.find((e) => e.id === t.engineerId)?.naam ?? '', t.startdatum ?? '', t.einddatum ?? '', t.van ?? '', t.tot ?? '', fmtUren(urenVan(t)), t.status === 'GEPLAND' ? (t.terugmelding ?? 'Gepland') : 'Niet gepland']);
    const tsv = [`Project\t${p.nummer} ${p.naam}`, `Klant\t${p.klant ?? ''}`, `Status\t${PROJECT_STATUS[p.status]}`, `Urenbudget\t${budget}`, `Ingepland\t${fmtUren(gepland)}`, '', kop.join('\t'), ...rij.map((r) => r.join('\t')), '', 'Gemaakt met bsmartr – app.bsmartr.nl'].join('\n');
    await navigator.clipboard.writeText(tsv);
    toast('Overzicht staat op het klembord – plak het in Excel', 'succes');
  };

  return (
    <div className="pagina kolom" style={{ gap: 16 }}>
      <div className="rij wrap geen-print paginakop-knoppen">
        <div className="groei" />
        {mag('projecten.schrijven') && <button className="knop" onClick={() => setBewerk(true)}><Icoon naam="settings" maat={16} /> Bewerken</button>}
        <Link className="knop" to={`/projecten/${p.id}/werkpakketten`}><Icoon naam="folder-kanban" maat={16} /> Werkzaamheden</Link>
        <Link className="knop" to={`/projecten/${p.id}/planning`}><Icoon naam="chart-gantt" maat={16} /> Projectplanning</Link>
        <Link className="knop primair" to={`/projecten/${p.id}/rapport`} title="Statusrapport om te delen of af te drukken"><Icoon naam="file-chart-column" maat={16} /> Rapport</Link>
        <MeerMenu>
          {(sluit) => (
            <>
              {mag('taken.aanmaken') && <Link to={`/taken/nieuw?project=${p.id}`} state={taak.state}><Icoon naam="list-checks" maat={16} /> Losse taak</Link>}
              <button onClick={() => { sluit(); window.print(); }}><Icoon naam="printer" maat={16} /> Afdrukken</button>
              <button onClick={() => { sluit(); void excel(); }}><Icoon naam="layout-grid" maat={16} /> Kopiëren naar Excel</button>
              {p.teamsLink ? <a href={p.teamsLink} target="_blank" rel="noreferrer"><Icoon naam="external-link" maat={16} /> Teams intern</a> : <span className="uit" title="Vul de link in via Bewerken"><Icoon naam="external-link" maat={16} /> Teams intern (geen link)</span>}
              {p.teamsExternLink ? <a href={p.teamsExternLink} target="_blank" rel="noreferrer"><Icoon naam="external-link" maat={16} /> Teams extern</a> : <span className="uit" title="Vul de link in via Bewerken"><Icoon naam="external-link" maat={16} /> Teams extern (geen link)</span>}
              {mag('projecten.schrijven') && <div style={{ padding: '6px 4px 2px', borderTop: '1px solid var(--lijn-2)', marginTop: 4 }}><VerwijderKnop label="Project verwijderen" klein onBevestig={() => { sluit(); verwijder.mutate(); }} /></div>}
            </>
          )}
        </MeerMenu>
      </div>

      <div className="paneel">
        <div className="rij wrap" style={{ marginBottom: 10 }}>
          <h1>
            {p.nummer} · {p.naam}
          </h1>
          <Badge toon="accent">{PROJECT_STATUS[p.status]}</Badge>
        </div>
        <div className="grid4">
          <Info label="Klant" waarde={p.klant} />
          <Info label="Locatie" waarde={p.geoNaam ?? p.locatie} />
          <Info label="Projectmanager" waarde={[p.pmNaam, p.pmEmail, p.pmTelefoon].filter(Boolean).join(' · ')} />
          <Info label="Contactpersoon" waarde={[p.contactpersoon, p.contactEmail].filter(Boolean).join(' · ')} />
          <Info label="Start" waarde={fmtDag(p.start, 'jaar')} />
          <Info label="Oplevering" waarde={fmtDag(p.oplevering, 'jaar')} />
          <Info label="Livegang" waarde={fmtDag(p.livegang, 'jaar')} />
          <Info
            label="Prognose"
            waarde={
              prog?.laterDanHorizon
                ? 'later dan 6 maanden'
                : totaalProg?.later
                  ? `onbekend (${agile.filter((x) => !x.eind).map((x) => x.naam).join(', ')}: nog geen sprint met team)`
                  : totaalProg?.datum
                    ? `${fmtDag(totaalProg.datum, 'jaar')}${agileBepalend ? ` (${agileBepalend.naam}, agile)` : prog?.bepalend ? ` (${prog.bepalend})` : ''}`
                    : 'alles ingepland'
            }
            rood={!!(totaalProg?.datum && p.livegang && totaalProg.datum > p.livegang)}
          />
        </div>
        {p.omschrijving && <p className="mute" style={{ whiteSpace: 'pre-wrap' }}>{p.omschrijving}</p>}
      </div>

      <div className="grid2" style={{ gridTemplateColumns: 'minmax(260px, 1fr) 2fr' }}>
        <div className="paneel kolom">
          <h2>Urenbudget · gepland · teruggemeld</h2>
          <div className="kaartjes">
            <Kaart getal={fmtUren(budget)} lbl="budget" />
            <Kaart getal={fmtUren(gepland)} lbl="ingepland" />
            <Kaart getal={fmtUren(rest)} lbl="rest" rood={rest < 0} />
          </div>
          <Balk waarde={gepland + nodig} max={budget} />
          <div className="klein">
            {(agBesteed > 0 || ag.nogTeDoen > 0) && <>agile: {fmtUren(agBesteed)}u geschreven, nog {fmtUren(ag.nogTeDoen)}u te doen volgens schatting · </>}✓ {fmtUren(klaar)}u als klaar teruggemeld · ⏳ {fmtUren(nodig)}u nog nodig volgens engineers · {fmtUren(wacht)}u wacht op terugmelding
          </div>
        </div>
        <div className="paneel" style={{ overflow: 'auto' }}>
          <h2 style={{ marginBottom: 8 }}>Uren per expertise & livegang</h2>
          <table className="tabel">
            <thead>
              <tr>
                <th>Expertise</th>
                <th className="rechts">Budget</th>
                <th className="rechts">Ingepland</th>
                <th className="rechts">Nog te doen</th>
                <th className="rechts">Beschikbaar tot livegang</th>
                <th>Haalbaar</th>
                <th>Verwacht klaar</th>
              </tr>
            </thead>
            <tbody>
              {prog?.perExpertise.map((x) => (
                <tr key={x.expertise}>
                  <td>{x.expertise}</td>
                  <td className="rechts">{fmtUren(x.budget)}</td>
                  <td className="rechts">{fmtUren(x.ingepland)}</td>
                  <td className="rechts">{fmtUren(x.rest)}</td>
                  <td className="rechts">{p.livegang ? fmtUren(x.beschikbaar) : '–'}</td>
                  <td>{x.haalbaar == null ? '–' : x.haalbaar ? <Badge toon="groen">ja</Badge> : <Badge toon="rood">nee</Badge>}</td>
                  <td title={`Team van ${x.team} engineer(s)`}>{x.rest <= 0 ? 'klaar' : x.klaar ? fmtDag(x.klaar, 'jaar') : 'later dan 6 maanden'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!p.expertiseBudget.length && <div className="leeg">Geen budget per expertise ingevuld.</div>}
        </div>
      </div>

      <div className="paneel">
        <div className="sectie-kop">
          <h2 className="groei">Taken van dit project ({taken.length})</h2>
          <button className="knop" onClick={() => setPop('taken')}><Icoon naam="list-checks" maat={16} /> Taken</button>
          <button className="knop" onClick={() => setPop('tijdlijn')}><Icoon naam="chart-gantt" maat={16} /> Tijdlijn</button>
        </div>
        <TakenTabel taken={taken.slice(0, 8)} toonProject={false} />
        {taken.length > 8 && (
          <button className="knop klein" style={{ marginTop: 8 }} onClick={() => setPop('taken')}>
            Alle {taken.length} taken tonen
          </button>
        )}
      </div>

      {/* SPEC §19 en §18 */}
      <EigenVeldenOverzicht entiteit="PROJECT" waarden={p.velden} />
      <div className="paneel"><Gesprek entiteit="PROJECT" id={p.id} /></div>

      {pop && (
        <Modal
          breedte="vol"
          titel={`${p.nummer} · ${pop === 'taken' ? 'Taken' : 'Tijdlijn'}`}
          onSluit={() => setPop(null)}
          extraKop={
            <div className="rij">
              <button className={`knop ${pop === 'taken' ? 'aan' : ''}`} onClick={() => setPop('taken')}><Icoon naam="list-checks" maat={16} /> Taken</button>
              <button className={`knop ${pop === 'tijdlijn' ? 'aan' : ''}`} onClick={() => setPop('tijdlijn')}><Icoon naam="chart-gantt" maat={16} /> Tijdlijn</button>
            </div>
          }
        >
          {pop === 'taken' ? <TakenTabel taken={taken} toonProject={false} /> : <ProjectGantt d={d} project={p} labelBreedte={360} />}
        </Modal>
      )}
      {bewerk && <ProjectForm project={p} onSluit={() => setBewerk(false)} />}
    </div>
  );
}

function Info({ label, waarde, rood }: { label: string; waarde?: string | null; rood?: boolean }) {
  return (
    <div>
      <div className="klein mute vet">{label}</div>
      <div style={{ color: rood ? 'var(--rood)' : undefined }}>{waarde || '–'}</div>
    </div>
  );
}

function Kaart({ getal, lbl, rood }: { getal: string; lbl: string; rood?: boolean }) {
  return (
    <div className="kaartje">
      <div className="getal" style={{ color: rood ? 'var(--rood)' : undefined }}>
        {getal}
      </div>
      <div className="lbl">{lbl}</div>
    </div>
  );
}

/** Teams-knop; zonder link grijs met uitleg waar je hem invult. */
